import DashboardLayout from '@/components/DashboardLayout';
import { useForm } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Users, Package, CreditCard, Percent, Check, ChevronsUpDown, Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useMemo } from 'react';

interface Student {
  id: number;
  name: string;
  email: string;
}

interface LessonPackage {
  id: number;
  name: string;
  description: string;
  lessons_count: number;
  price: string;
  formatted_price: string;
  price_per_lesson: number;
}

interface Props {
  students: Student[];
  lesson_packages: LessonPackage[];
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Dinheiro' },
  { value: 'credit_card', label: 'Cartão de Crédito' },
  { value: 'debit_card', label: 'Cartão de Débito' },
  { value: 'pix', label: 'PIX' },
  { value: 'bank_transfer', label: 'Transferência Bancária' },
];

const PAYMENT_STATUS = [
  { value: 'paid', label: 'Pago' },
  { value: 'pending', label: 'Pendente' },
];

export default function New({ students, lesson_packages }: Props) {
  const [selectedPackage, setSelectedPackage] = useState<LessonPackage | null>(null);
  const [studentComboboxOpen, setStudentComboboxOpen] = useState(false);
  const [packageComboboxOpen, setPackageComboboxOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<{ valid: boolean; discount: number; description?: string; code?: string } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  
  const { data, setData, post, processing, errors } = useForm({
    student_id: '',
    lesson_package_id: '',
    payment_method: 'cash',
    status: 'paid',
    discount_percentage: '0',
  });

  // Calcula o preço final com desconto
  const calculatedPrice = useMemo(() => {
    if (!selectedPackage) return null;
    
    const originalPrice = parseFloat(selectedPackage.price);
    const discount = parseFloat(data.discount_percentage) || 0;
    const discountAmount = (originalPrice * discount) / 100;
    const finalPrice = originalPrice - discountAmount;
    
    return {
      original: originalPrice,
      discount: discount,
      discountAmount: discountAmount,
      final: finalPrice,
    };
  }, [selectedPackage, data.discount_percentage]);

  const validateCoupon = async () => {
    if (!couponCode.trim() || !selectedPackage) return;
    setValidatingCoupon(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const res = await fetch('/api/v1/students/coupons/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ code: couponCode, package_price: parseFloat(selectedPackage.price) })
      });
      const json = await res.json();
      if (json.success) {
        setCouponResult({ valid: true, discount: json.data.discount_amount, description: json.data.description, code: json.data.code });
      } else {
        setCouponResult({ valid: false, discount: 0, description: json.error });
      }
    } catch {
      setCouponResult({ valid: false, discount: 0, description: 'Erro ao validar cupom' });
    }
    setValidatingCoupon(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post('/student_subscriptions');
  };

  const handlePackageChange = (packageId: string) => {
    setData('lesson_package_id', packageId);
    const pkg = lesson_packages.find(p => p.id.toString() === packageId);
    setSelectedPackage(pkg || null);
  };

  return (
    <DashboardLayout>
    <div className="p-8 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            Criar Nova Assinatura
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Atribua um pacote de aulas a um aluno e registre o pagamento
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Seleção de Aluno */}
            <div className="space-y-2">
              <Label htmlFor="student_id">Aluno *</Label>
              <Popover open={studentComboboxOpen} onOpenChange={setStudentComboboxOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={studentComboboxOpen}
                    className={cn(
                      "border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs h-9",
                      "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none",
                      !data.student_id && "text-muted-foreground"
                    )}
                  >
                    <span className="truncate">
                      {data.student_id
                        ? students.find((s) => s.id.toString() === data.student_id)?.name
                        : "Selecione um aluno"}
                    </span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="!w-[var(--radix-popover-trigger-width)] !p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar aluno..." />
                    <CommandList>
                      <CommandEmpty>Nenhum aluno encontrado.</CommandEmpty>
                      <CommandGroup>
                        {students.map((student) => (
                          <CommandItem
                            key={student.id}
                            value={student.name}
                            onSelect={() => {
                              setData('student_id', student.id.toString());
                              setStudentComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                data.student_id === student.id.toString() ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div>
                              <div className="font-medium">{student.name}</div>
                              <div className="text-xs text-muted-foreground">{student.email}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.student_id && (
                <p className="text-sm text-destructive">{errors.student_id}</p>
              )}
            </div>

            {/* Seleção de Pacote */}
            <div className="space-y-2">
              <Label htmlFor="lesson_package_id">Pacote de Aulas *</Label>
              <Popover open={packageComboboxOpen} onOpenChange={setPackageComboboxOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={packageComboboxOpen}
                    className={cn(
                      "border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs h-9",
                      "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none",
                      !data.lesson_package_id && "text-muted-foreground"
                    )}
                  >
                    <span className="truncate">
                      {selectedPackage ? selectedPackage.name : "Selecione um pacote"}
                    </span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="!w-[var(--radix-popover-trigger-width)] !p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar pacote..." />
                    <CommandList>
                      <CommandEmpty>Nenhum pacote encontrado.</CommandEmpty>
                      <CommandGroup>
                        {lesson_packages.map((pkg) => (
                          <CommandItem
                            key={pkg.id}
                            value={pkg.name}
                            onSelect={() => {
                              handlePackageChange(pkg.id.toString());
                              setPackageComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                data.lesson_package_id === pkg.id.toString() ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div>
                              <div className="font-medium">{pkg.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {pkg.lessons_count} {pkg.lessons_count === 1 ? 'aula' : 'aulas'} • {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                }).format(parseFloat(pkg.price))}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.lesson_package_id && (
                <p className="text-sm text-destructive">{errors.lesson_package_id}</p>
              )}
            </div>

            {/* Detalhes do Pacote Selecionado */}
            {selectedPackage && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Package className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2">{selectedPackage.name}</h3>
                      {selectedPackage.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {selectedPackage.description}
                        </p>
                      )}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Total de Aulas</p>
                          <p className="text-lg font-bold text-primary">
                            {selectedPackage.lessons_count}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Preço Original</p>
                          <p className="text-lg font-bold text-muted-foreground">
                            {new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            }).format(parseFloat(selectedPackage.price))}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Por Aula</p>
                          <p className="text-lg font-bold">
                            {new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            }).format(selectedPackage.price_per_lesson)}
                          </p>
                        </div>
                      </div>

                      {/* Mostrar desconto e preço final se houver desconto */}
                      {calculatedPrice && calculatedPrice.discount > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Desconto</p>
                              <p className="text-lg font-bold text-orange-600">
                                -{calculatedPrice.discount}%
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Valor do Desconto</p>
                              <p className="text-lg font-bold text-orange-600">
                                -{new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                }).format(calculatedPrice.discountAmount)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Preço Final</p>
                              <p className="text-lg font-bold text-green-700">
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                }).format(calculatedPrice.final)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Informações de Pagamento */}
            <div className="border-t pt-6">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-lg">Informações de Pagamento</h3>
              </div>

              <div className="space-y-4">
                {/* Cupom de Desconto */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Ticket className="w-4 h-4" />
                    Cupom de Desconto
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
                      placeholder="Código do cupom"
                      className="font-mono uppercase"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={validateCoupon}
                      disabled={!couponCode.trim() || !selectedPackage || validatingCoupon}
                    >
                      {validatingCoupon ? 'Validando...' : 'Aplicar'}
                    </Button>
                  </div>
                  {couponResult && (
                    couponResult.valid ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-green-600">
                          -{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(couponResult.discount)}
                        </Badge>
                        <span className="text-sm text-green-600">{couponResult.description || 'Cupom aplicado!'}</span>
                      </div>
                    ) : (
                      <p className="text-sm text-destructive">{couponResult.description}</p>
                    )
                  )}
                </div>

                {/* Campo de Desconto */}
                <div className="space-y-2">
                  <Label htmlFor="discount_percentage" className="flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    Desconto (%)
                  </Label>
                  <Input
                    id="discount_percentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={data.discount_percentage}
                    onChange={(e) => setData('discount_percentage', e.target.value)}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">
                    Opcional: Aplique um desconto de 0% a 100%. Use 100% para aula cortesia.
                  </p>
                  {errors.discount_percentage && (
                    <p className="text-sm text-destructive">{errors.discount_percentage}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment_method">Método de Pagamento *</Label>
                    <Select
                      value={data.payment_method}
                      onValueChange={(value) => setData('payment_method', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((method) => (
                          <SelectItem key={method.value} value={method.value}>
                            {method.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.payment_method && (
                      <p className="text-sm text-destructive">{errors.payment_method}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status do Pagamento *</Label>
                    <Select
                      value={data.status}
                      onValueChange={(value) => setData('status', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_STATUS.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.status && (
                      <p className="text-sm text-destructive">{errors.status}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => window.history.back()}
              >
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? 'Criando...' : 'Criar Assinatura'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
